import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Copy, Flame, Gauge, KeyRound, Plus, Radio, ShieldCheck, Trash2, Webhook } from 'lucide-react';
import { outreachApi } from '../api/outreachApi';
import InfoHint from '../components/InfoHint';
import { SUPPORT_EMAIL } from '../../constants/support';
import '../outreach.css';

function Section({ icon: Icon, title, children }) {
  return (
    <section className="outreach-settings-section">
      <div className="outreach-settings-section-header">
        <Icon size={16} />
        <h2>{title}</h2>
      </div>
      <div className="outreach-settings-section-content">{children}</div>
    </section>
  );
}

function Usage({ label, used, cap, hint }) {
  const tight = used / cap >= 0.8;
  return (
    <div className={`metric${tight ? ' metric-alert' : ''}`}>
      <span className="metric-value">
        {used} / {cap}
      </span>
      <span className="metric-label">
        {label}
        {hint && <InfoHint text={hint} />}
      </span>
    </div>
  );
}

export default function OutreachSettingsPage() {
  const { t } = useTranslation('common');

  const [settings, setSettings] = useState(null);
  const [webhooks, setWebhooks] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [keys, setKeys] = useState([]);
  const [channels, setChannels] = useState([]);
  const [features, setFeatures] = useState(null);
  const [limits, setLimits] = useState(null);
  const [newSecret, setNewSecret] = useState(null);
  const [newKey, setNewKey] = useState(null);
  const [hookForm, setHookForm] = useState({ url: '', events: [] });
  const [keyName, setKeyName] = useState('');
  const [alertEmail, setAlertEmail] = useState('');
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);


  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, w, e, k, c, l, f] = await Promise.all([
        outreachApi.getWorkspaceSettings(),
        outreachApi.listWebhooks(),
        outreachApi.listEventTypes(),
        outreachApi.listApiKeys(),
        outreachApi.listChannels(),
        outreachApi.myLimits(),
        outreachApi.features(),
      ]);
      setLimits(l);
      setFeatures(f);
      setSettings(s);
      setAlertEmail(s.notification_email || '');
      setWebhooks(w);
      setEventTypes(e);
      setKeys(k);
      setChannels(c);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleAi = async () => {
    setSettings(await outreachApi.updateWorkspaceSettings({ ai_content_enabled: !settings.ai_content_enabled }));
  };

  const saveAlertEmail = async () => {
    setSavingAlerts(true);
    setError(null);
    try {
      setSettings(await outreachApi.updateWorkspaceSettings({ notification_email: alertEmail.trim() }));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSavingAlerts(false);
    }
  };

  const toggleAlert = async (field) => {
    setSettings(await outreachApi.updateWorkspaceSettings({ [field]: !settings[field] }));
  };

  const toggleEvent = (name) =>
    setHookForm({
      ...hookForm,
      events: hookForm.events.includes(name)
        ? hookForm.events.filter((x) => x !== name)
        : [...hookForm.events, name],
    });

  const addWebhook = async () => {
    setError(null);
    try {
      const created = await outreachApi.createWebhook({ url: hookForm.url, events: hookForm.events });
      setNewSecret(created.secret);
      setHookForm({ url: '', events: [] });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  };

  const addKey = async () => {
    setError(null);
    try {
      const created = await outreachApi.createApiKey(keyName);
      setNewKey(created.secret);
      setKeyName('');
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  };

  const testHook = async (hook) => {
    const result = await outreachApi.testWebhook(hook.id);
    window.alert(t('outreach.settings.testResult', { count: result.delivered }));
    await load();
  };

  if (loading) return <div className="unibox"><div className="unibox-empty">{t('outreach.loading')}</div></div>;

  // The outreach service can be down while the rest of the app is fine -
  // load() catches and stops here, so nothing below may assume these
  // loaded (Promise.all means it is all four or none of them).
  if (!settings || !limits || !features) {
    return (
      <div className="unibox">
        <div className="unibox-header">
          <h1 className="unibox-title">{t('outreach.settings.title')}</h1>
        </div>
        {error && <div className="unibox-banner">{String(error)}</div>}
      </div>
    );
  }

  return (
    <div className="unibox">
      <div className="unibox-header">
        <h1 className="unibox-title">{t('outreach.settings.title')}</h1>
      </div>

      {error && <div className="unibox-banner">{String(error)}</div>}

      <div className="outreach-settings-body">
      <Section icon={Gauge} title={t('outreach.settings.limitsHeading')}>
        {limits.sending_frozen && (
          <div className="unibox-banner">
            {t('outreach.settings.frozenBanner', { reason: limits.frozen_reason || '-' })}
          </div>
        )}
        <div className="metric-row">
          <Usage label={t('outreach.mailboxes')} used={limits.mailboxes} cap={limits.limits.max_mailboxes} />
          <Usage label={t('outreach.contacts')} used={limits.contacts} cap={limits.limits.max_contacts} />
          <Usage
            label={t('outreach.campaign.title')}
            used={limits.campaigns}
            cap={limits.limits.max_campaigns}
          />
          <Usage
            label={t('outreach.settings.sendsToday')}
            used={limits.sends_today}
            cap={limits.limits.max_sends_per_day}
            hint={t('outreach.settings.hints.sendsToday')}
          />
        </div>
        {limits.sending_frozen ? (
          <button
            className="unibox-refresh"
            onClick={async () => {
              await outreachApi.unfreezeSending();
              await load();
            }}
          >
            {t('outreach.settings.unfreeze')}
          </button>
        ) : (
          <button
            className="unibox-page-button"
            onClick={async () => {
              const reason = window.prompt(t('outreach.settings.freezePrompt'));
              if (!reason) return;
              await outreachApi.freezeSending(reason);
              await load();
            }}
          >
            {t('outreach.settings.freeze')}
          </button>
        )}
        <p className="unibox-reply-hint">{t('outreach.settings.freezeExplainer')}</p>
      </Section>

      <Section icon={Bell} title={t('outreach.settings.alertsHeading')}>
        <p className="unibox-reply-hint">{t('outreach.settings.alertsExplainer')}</p>
        <div className="lead-filters">
          <input
            className="unibox-select settings-alert-email"
            type="email"
            placeholder={t('outreach.settings.alertEmailPlaceholder')}
            value={alertEmail}
            onChange={(e) => setAlertEmail(e.target.value)}
          />
          <button
            className="unibox-refresh"
            onClick={saveAlertEmail}
            disabled={savingAlerts || alertEmail.trim() === (settings.notification_email || '')}
          >
            {savingAlerts ? t('outreach.settings.saving') : t('outreach.settings.saveAlertEmail')}
          </button>
        </div>
        {!settings.notification_email && (
          <div className="unibox-banner">{t('outreach.settings.alertsOff')}</div>
        )}
        <div className="settings-toggles">
        <label className="campaign-checkbox">
          <input
            type="checkbox"
            checked={settings.notify_on_reply}
            disabled={!settings.notification_email}
            onChange={() => toggleAlert('notify_on_reply')}
          />
          {t('outreach.settings.alertOnReply')}
        </label>
        <label className="campaign-checkbox">
          <input
            type="checkbox"
            checked={settings.notify_on_campaign_paused}
            disabled={!settings.notification_email}
            onChange={() => toggleAlert('notify_on_campaign_paused')}
          />
          {t('outreach.settings.alertOnCampaignPaused')}
        </label>
        <label className="campaign-checkbox">
          <input
            type="checkbox"
            checked={settings.notify_on_mailbox_broken}
            disabled={!settings.notification_email}
            onChange={() => toggleAlert('notify_on_mailbox_broken')}
          />
          {t('outreach.settings.alertOnMailboxBroken')}
        </label>
        </div>
      </Section>

      <Section icon={ShieldCheck} title={t('outreach.settings.privacyHeading')}>
        <label className="campaign-checkbox">
          <input type="checkbox" checked={settings.ai_content_enabled} onChange={toggleAi} />
          {t('outreach.settings.aiContent')}
          <InfoHint text={t('outreach.settings.hints.aiContent')} />
        </label>
      </Section>

      <Section icon={Flame} title={t('outreach.settings.warmupHeading')}>
        {features && features.warmup && (
          <p className="unibox-reply-hint">{t('outreach.settings.warmupOn')}</p>
        )}
        {features && !features.warmup && features.warmup_can_request && (
          <div className="unibox-banner">
            {t('outreach.settings.warmupOffer')}{' '}
            <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Warm-up request')}`}>
              {SUPPORT_EMAIL}
            </a>
          </div>
        )}
        {features && !features.warmup && !features.warmup_can_request && (
          <p className="unibox-reply-hint">{t('outreach.settings.warmupLocked')}</p>
        )}
      </Section>

      <Section icon={Radio} title={t('outreach.settings.channelsHeading')}>
        <div className="metric-row">
          {channels.map((channel) => (
            <div key={channel.kind} className="metric">
              <span className="metric-value">{channel.kind}</span>
              <span className="metric-label">
                {channel.available ? t('outreach.settings.channelReady') : t('outreach.settings.channelSoon')}
              </span>
              <span className="lead-history-source">{channel.note}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section icon={Webhook} title={t('outreach.settings.webhooksHeading')}>
        <p className="unibox-reply-hint">{t('outreach.settings.webhooksExplainer')}</p>

        {newSecret && (
          <div className="unibox-banner">
            {t('outreach.settings.secretOnce')}: <code>{newSecret}</code>{' '}
            <button className="unibox-page-button" onClick={() => navigator.clipboard.writeText(newSecret)}>
              <Copy size={12} />
            </button>
          </div>
        )}

        {webhooks.length === 0 && <div className="unibox-empty">{t('outreach.settings.noWebhooks')}</div>}
        {webhooks.map((hook) => (
          <div key={hook.id} className="lead-detail-row">
            <span>{hook.url}</span>
            <span className="unibox-badge">{hook.events.join(', ')}</span>
            <span className={hook.active ? 'unibox-badge' : 'unibox-error'}>
              {hook.active ? t('outreach.settings.active') : t('outreach.settings.disabled')}
            </span>
            {hook.last_error && <span className="unibox-error">{hook.last_error}</span>}
            <button className="unibox-page-button" onClick={() => testHook(hook)}>
              {t('outreach.settings.test')}
            </button>
            <button
              className="unibox-page-button"
              onClick={async () => {
                await outreachApi.deleteWebhook(hook.id);
                await load();
              }}
            >
              <Trash2 size={12} />
            </button>
          </div>
        ))}

        <div className="lead-filters">
          <input
            className="unibox-select"
            placeholder="https://example.com/hooks/chatscontrol"
            value={hookForm.url}
            onChange={(e) => setHookForm({ ...hookForm, url: e.target.value })}
          />
          {eventTypes.map((name) => (
            <label key={name} className="campaign-checkbox">
              <input type="checkbox" checked={hookForm.events.includes(name)} onChange={() => toggleEvent(name)} />
              {name}
            </label>
          ))}
          <button
            className="unibox-refresh"
            onClick={addWebhook}
            disabled={!hookForm.url || hookForm.events.length === 0}
          >
            <Plus size={13} /> {t('outreach.settings.addWebhook')}
          </button>
        </div>
      </Section>

      <Section icon={KeyRound} title={t('outreach.settings.apiKeysHeading')}>
        <p className="unibox-reply-hint">{t('outreach.settings.apiKeysExplainer')}</p>

        {newKey && (
          <div className="unibox-banner">
            {t('outreach.settings.secretOnce')}: <code>{newKey}</code>{' '}
            <button className="unibox-page-button" onClick={() => navigator.clipboard.writeText(newKey)}>
              <Copy size={12} />
            </button>
          </div>
        )}

        {keys.length === 0 && <div className="unibox-empty">{t('outreach.settings.noKeys')}</div>}
        {keys.map((key) => (
          <div key={key.id} className="lead-detail-row">
            <span>{key.name}</span>
            <span className="lead-history-source">{key.prefix}...</span>
            <span className="lead-history-source">
              {key.last_used_at
                ? t('outreach.settings.lastUsed', { date: new Date(key.last_used_at).toLocaleString() })
                : t('outreach.settings.neverUsed')}
            </span>
            {key.revoked_at ? (
              <span className="unibox-error">{t('outreach.settings.revoked')}</span>
            ) : (
              <button
                className="unibox-page-button"
                onClick={async () => {
                  await outreachApi.revokeApiKey(key.id);
                  await load();
                }}
              >
                {t('outreach.settings.revoke')}
              </button>
            )}
          </div>
        ))}

        <div className="lead-filters">
          <input
            className="unibox-select"
            placeholder={t('outreach.settings.keyName')}
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
          />
          <button className="unibox-refresh" onClick={addKey} disabled={!keyName.trim()}>
            <Plus size={13} /> {t('outreach.settings.addKey')}
          </button>
        </div>
      </Section>
      </div>
    </div>
  );
}
